import {
  Dialog,
  DialogDisclosure,
  DialogHeading,
  DialogProvider,
  Heading,
  HeadingLevel,
  useDialogStore,
} from "@ariakit/react";
import { DocumentTitle } from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import type { ReactNode } from "react";
import { Link } from "wouter";
import { noBreak } from "../../common/common.css.ts";
import { hrefs } from "../../hrefs.ts";
import { Divider } from "../forms/Divider.tsx";
import { useCloseOnInteract } from "../Layout/useCloseOnInteract.tsx";
import {
  HeaderBackButton,
  HeaderTitle,
  PageContainer,
  PageHeader,
} from "../Page/Page.tsx";
import capSrc from "./cap.svg";
import { MovementDiagram } from "./Diagram.tsx";
import measurePhotoSrc from "./measurePhoto.webp";
import pinchSrc from "./pinch.svg";
import shieldSrc from "./shield.svg";
import shoveSrc from "./shove.svg";
import skullSrc from "./skull.svg";
import {
  buy,
  header,
  note,
  page,
  section,
  table,
  toc,
  tower,
} from "./style.css.ts";
import { ThrowdownSimulator } from "./ThrowdownSimulator.tsx";
import thumpSrc from "./thump.svg";
import towerUrl from "./tower.webp";
import { useScrollToAnchor } from "./useScrollToAnchor.ts";
import wienerSrc from "./wiener.svg";

export function Section(
  props: { children: ReactNode } & RecipeVariants<typeof section.container>,
) {
  return (
    <div className={section.container({ theme: props.theme })}>
      <div className={section.content}>{props.children}</div>
    </div>
  );
}

function SetupSection() {
  return (
    <Section>
      <Heading id="setting-up" className={section.heading()}>
        Setting Up
      </Heading>

      <p className={section.paragraph()}>
        <strong>Give everyone 3 orcs</strong> armed with either a pistol and
        crude hand weapon, or a blaster. Orcs are toughness 3 and throw 3D6
        scoot dice.
      </p>

      <p className={section.paragraph()}>
        <strong>Clear a 2’ by 2’ play area</strong> and set up some terrain.
        Each of you drops 6 bottle caps into play from a decent height. Put 1
        more cap in the middle.
      </p>

      <p className={section.paragraph()}>
        <strong>Starting with whoever last finished a drink,</strong> take turns
        to place one orc at a time, anywhere in play at least 4” from all caps
        and enemies.
      </p>

      <p className={section.paragraph()}>
        <strong>Start a timer for 30 minutes.</strong> The last boss to deploy
        something activates the first model.
      </p>
    </Section>
  );
}

function TowerSection() {
  return (
    <Section>
      <img
        src={towerUrl}
        alt=""
        style={{ shapeOutside: `url("${towerUrl}")` }}
        className={tower.image}
      />

      <Heading id="tower" className={section.heading()}>
        The Tower
      </Heading>

      <p className={section.paragraph()}>
        In <em>Space Gits</em> each model has to drag around their own tottering{" "}
        <em>tower</em> of dice. When a model takes damage, and when they
        activate, you add six-sided dice to their <em>tower</em>, on top of any
        other dice they are lugging about.
      </p>

      <p className={section.paragraph({ breakout: true })}>
        <strong>
          The <em>tower</em> has to touch the model at all times.
        </strong>{" "}
        When you move a model, you have to move their <em>tower</em> with them.
      </p>

      <p className={section.paragraph()}>
        If a boss causes their own <em>tower</em> to fall: that git{" "}
        <em>falls over</em>. Knock the model over on its ass. If that happens
        during a git’s activation, their activation is over. If a boss causes
        anyone else’s <em>tower</em> to fall, the dislodged dice are discarded,
        but the model remains standing. A <em>fallen</em> git can’t be picked to
        activate until they’re <em>booted</em> back onto their feet again.
      </p>

      <p className={section.paragraph()}>
        When an orc takes <em>damage</em>, for each point of <em>damage</em>{" "}
        received, your opponent gives you a D6 to add to your damaged model’s{" "}
        <em>tower</em>.
      </p>
    </Section>
  );
}

function ActivationSection() {
  return (
    <Section theme="green">
      <Heading id="activating" className={section.heading()}>
        Activating
      </Heading>

      <p className={section.paragraph()}>
        <strong>You activate an orc and then I activate an orc.</strong>
      </p>

      <p className={section.paragraph()}>
        When you activate an orc, they do one or two <i>different</i> actions
        out of <em>scoot</em>, <em>shoot</em> and <em>boot</em> (in any order).
        When your orc has done their business, they <em>take a swig</em>: add a
        D6 to the top of their <em>tower</em>.
      </p>
    </Section>
  );
}

function ScootSection() {
  return (
    <Section>
      <Heading id="scooting" className={section.heading()}>
        Scooting
      </Heading>

      <p className={section.paragraph()}>
        To <em>scoot</em> an orc, roll 3D6 into the play area as{" "}
        <em>scoot dice</em>. You gotta roll them all at once. One die at a time
        (in any order), move your orc directly towards that die a number of
        inches equal to the value of that die and then discard it. Move the full
        distance if you can. You have to use all your <em>scoot dice</em>.
      </p>

      <p className={section.paragraph()}>
        As you move your orc, make sure the little tower of dice is touching
        them at all times. If any of the dice fall, so do they.
      </p>

      <MovementDiagram />

      <div className={section.group} style={{ clear: "both" }}>
        <div className={note.container({ theme: "blue" })}>
          <div className={note.loot}>
            <img src={capSrc} alt="" className={note.lootGraphic} />
            Loot! Loot! Loot! Loot!
          </div>
          <p style={{ fontSize: "1.125rem" }}>
            If you bump into a bottle cap while moving, you stop and pick it up.
          </p>
        </div>

        <div className={note.container({ theme: "yellow" })}>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              inlineSize: "fit-content",
              marginInline: "auto",
              marginBlockEnd: "0.5rem",
            }}
          >
            <span
              className={note.fight({
                size: "large",
                rotate: "-25deg",
                stagger: "1st",
              })}
            >
              Fight!
            </span>
            <span className={note.fight({ rotate: "-12deg", stagger: "2nd" })}>
              Fight!
            </span>
          </div>

          <p style={{ fontSize: "1.125rem" }}>
            If you bump into one of my orcs, a <em>fight</em> breaks out, and
            your activation is over.
          </p>

          <div
            style={{
              display: "flex",
              gap: "1rem",
              inlineSize: "fit-content",
              marginInline: "auto",
              marginBlockStart: "1rem",
            }}
          >
            <div className={note.fight({ rotate: "8deg", stagger: "3rd" })}>
              Fight!
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function FightSection() {
  const thumpImg = (
    <img className={section.inlineImage} src={thumpSrc} alt="" />
  );
  const pinchImg = (
    <img className={section.inlineImage} src={pinchSrc} alt="" />
  );
  const shoveImg = (
    <img className={section.inlineImage} src={shoveSrc} alt="" />
  );

  const thump = (
    <span className={noBreak}>
      {thumpImg} <em>thump</em>
    </span>
  );

  const shove = (
    <span className={noBreak}>
      {shoveImg} <em>shove</em>
    </span>
  );

  const pinch = (
    <span className={noBreak}>
      {pinchImg} <em>pinch</em>
    </span>
  );

  return (
    <Section>
      <Heading id="fighting" className={section.heading()}>
        Fighting
      </Heading>

      <p className={section.paragraph()}>
        When two models <em>fight</em>, their bosses count “3, 2, 1... go” then
        throw {thump}, {shove} or {pinch} with their hands.
      </p>

      <div className={section.group}>
        <p className={section.paragraph()}>
          {thump} the other model, unless the other boss threw {shove}.
        </p>

        <p className={section.paragraph({ explainer: true })}>
          To <em>thump</em>: roll all your fighting weapons’ <em>thump dice</em>
          . Do <em>big damage</em> with each die that equals or beats the other
          model’s <em>toughness</em>, and <em>weak damage</em> with the others.
        </p>
      </div>

      <div className={section.group}>
        <p className={section.paragraph()}>
          {shove} the other git back 3”, unless the other boss threw {pinch}.
        </p>

        <p className={section.paragraph({ explainer: true })}>
          The <em>shoved</em> git is moved by its boss, along with their{" "}
          <em>tower</em>. Push them directly away from their opponent.
        </p>
      </div>
      <div className={section.group}>
        <p className={section.paragraph()}>
          {pinch} 1 bottle cap from the other model’s boss, unless the other
          boss threw {thump}.
        </p>
      </div>

      <HeadingLevel>
        <ThrowdownSimulator />
      </HeadingLevel>

      <p className={section.paragraph()}>
        Once the throw is resolved, if the two models are still in contact (and
        both are still standing) they <em>fight</em> again until they are
        separated or one falls.
      </p>

      <div className={table.container({ theme: "blue" })}>
        <table className={table.content}>
          <colgroup>
            <col className={table.col({ size: "main" })} />
            <col style={{ inlineSize: "8ch" }} />
            <col style={{ inlineSize: "16ch" }} />
          </colgroup>

          <thead className={table.head}>
            <tr className={table.row}>
              <th className={table.cell}>Hand weapon</th>
              <th className={table.cell}>Thump Dice</th>
              <th className={table.cell}>Damage (Weak/Big)</th>
            </tr>
          </thead>
          <tbody className={table.body}>
            <tr className={table.row}>
              <th className={table.cell}>Orc Headbutt</th>
              <td className={table.cell}>1D6</td>
              <td className={table.cell}>1/2</td>
            </tr>
            <tr className={table.row}>
              <th className={table.cell}>Crude Hand Weapon</th>
              <td className={table.cell}>2D6</td>
              <td className={table.cell}>1/2</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function ShootSection() {
  return (
    <Section>
      <img
        className={section.image({ variant: "breakout" })}
        src={measurePhotoSrc}
        alt=""
      />
      <Heading id="shooting" className={section.heading()}>
        Shooting
      </Heading>

      <p className={section.paragraph()}>
        To <em>shoot</em>, roll your <em>bullet dice</em> into the play area.
        Each bullet travels in a straight line directly towards one of the{" "}
        <em>bullet dice</em> a number of inches equal to the gun’s{" "}
        <em>range</em>, hitting the first thing in its path (ignoring dice and{" "}
        <em>fallen</em> gits).
      </p>

      <ol className={section.steps}>
        <li className={section.step}>
          <p className={section.paragraph()}>
            If the thing that is hit is a model, compare the value of the{" "}
            <em>bullet die</em> to the <em>toughness</em> of that model.
          </p>
        </li>
        <li className={section.step}>
          <p className={section.paragraph()}>
            If the rolled value meets or beats the <em>toughness</em>, the
            unlucky model suffers <em>big damage</em> from the gun.
          </p>
        </li>
        <li className={section.step}>
          <p className={section.paragraph()}>
            If the value is lower than the model’s <em>toughness</em>, they
            suffer <em>weak damage</em>.
          </p>
        </li>
      </ol>

      <div className={table.container({ theme: "red" })}>
        <table className={table.content}>
          <colgroup>
            <col className={table.col({ size: "main" })} />
            <col style={{ inlineSize: "10ch" }} />
            <col style={{ inlineSize: "10ch" }} />
            <col style={{ inlineSize: "16ch" }} />
          </colgroup>

          <thead className={table.head}>
            <tr className={table.row}>
              <th className={table.cell}>Ranged weapon</th>
              <th className={table.cell}>Range</th>
              <th className={table.cell}>Bullet Dice</th>
              <th className={table.cell}>Damage (Weak/Big)</th>
            </tr>
          </thead>
          <tbody className={table.body}>
            <tr className={table.row}>
              <th className={table.cell}>Pistol</th>
              <td className={table.cell}>9&quot;</td>
              <td className={table.cell}>2D6</td>
              <td className={table.cell}>1/2</td>
            </tr>
            <tr className={table.row}>
              <th className={table.cell}>Blaster</th>
              <td className={table.cell}>18&quot;</td>
              <td className={table.cell}>1D8</td>
              <td className={table.cell}>2/3</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function BootSection() {
  return (
    <Section>
      <Heading id="booting" className={section.heading()}>
        Booting
      </Heading>

      <p className={section.paragraph()}>
        <strong>Put the boot in:</strong> A git in contact with a{" "}
        <em>fallen</em> enemy git can <em>put the boot in</em> to take 1 cap
        from the <em>fallen</em> git’s boss. The <em>fallen</em> git may then
        crawl away up to 2” in any direction.
      </p>

      <p className={section.paragraph()}>
        <strong>Boot up the arse:</strong> A git in contact with a{" "}
        <em>fallen</em> friendly git can <em>boot them up the arse</em> to get
        them to stand up.
      </p>
    </Section>
  );
}

function EndSection() {
  return (
    <Section theme="orange">
      <Heading
        id="ending-the-game"
        className={section.heading({ variant: "important" })}
      >
        Ending the game
      </Heading>

      <HeadingLevel>
        <section className={section.group}>
          <img className={section.graphic} src={shieldSrc} alt="" />

          <Heading className={section.heading()}>Sound of the Police</Heading>

          <p className={section.paragraph()}>
            When the 30 minute <em>timer</em> is up: grab 2D6{" "}
            <em>siren dice</em>. From now on, before each activation: the boss
            who is about to activate a model adds a D6 to the{" "}
            <em>siren dice</em> and then rolls them all...
          </p>

          <p className={section.paragraph()}>
            <strong>
              If they roll any triples, the game ends immediately.
            </strong>
          </p>
        </section>

        <section className={section.group}>
          <img className={section.graphic} src={skullSrc} alt="" />

          <Heading className={section.heading()}>Knocked Out</Heading>

          <p className={section.paragraph()}>
            A boss with no standing models is <em>knocked out</em> and the{" "}
            <em>timer</em> counts as being up. Once you are <em>knocked out</em>
            , you don’t roll the <em>siren pool</em>, but do add D6 to it each
            time you are passed over. If everyone is <em>knocked out</em>, the
            game ends.
          </p>
        </section>

        <section className={section.group}>
          <img className={section.graphic} src={wienerSrc} alt="" />

          <Heading className={section.heading()}>A Weiner Is You</Heading>

          <p className={section.paragraph()}>
            The boss that grabbed the most <em>bottle caps</em> wins the game!
          </p>

          <p className={section.paragraph()}>
            In the case of a tie, the tied bosses have a <em>build-off</em>. You
            get 10 seconds to stack the tallest dice tower. The boss with the
            tallest tower after 10secs wins the build-off and thus the game!
          </p>
        </section>
      </HeadingLevel>
    </Section>
  );
}

function BuySection() {
  return (
    <Section>
      <div className={buy.container}>
        <Heading id="buy" className={buy.heading}>
          <span className={buy.want}>Want</span>{" "}
          <span className={buy.more}>More?</span>
        </Heading>

        <p className={section.paragraph()}>
          Grab the full rules for loads more fun. Goblins, gnawballs, more
          weapons, fancy gear, rules for vehicles, campaign play, and so much
          more!
        </p>

        <Link href={hrefs.buy()} className={buy.action}>
          Get the full rules
        </Link>
      </div>
    </Section>
  );
}

function TableOfContents() {
  const store = useDialogStore();
  const closeOnInteract = useCloseOnInteract(store);

  return (
    <DialogProvider store={store}>
      <Dialog className={toc.dialog} modal={false} onClick={closeOnInteract}>
        <DialogHeading className={toc.header} render={<a href="#top" />}>
          <div className={toc.overline}>Quick Start Rules</div>
          <div className={toc.heading}>Table of Contents</div>
        </DialogHeading>

        <ul className={toc.list}>
          <li>
            <a className={toc.item} href="#setting-up">
              Setting Up
            </a>
          </li>
          <li>
            <a className={toc.item} href="#tower">
              The Tower
            </a>
          </li>
          <li>
            <a className={toc.item} href="#activating">
              Activating
            </a>
          </li>
          <li>
            <a className={toc.item} href="#scooting">
              Scooting
            </a>
          </li>
          <li>
            <a className={toc.item} href="#fighting">
              Fighting
            </a>
          </li>
          <li>
            <a className={toc.item} href="#shooting">
              Shooting
            </a>
          </li>
          <li>
            <a className={toc.item} href="#booting">
              Booting
            </a>
          </li>
          <li>
            <a className={toc.item} href="#ending-the-game">
              Ending the Game
            </a>
          </li>
        </ul>
        <Link href={hrefs.buy()} className={toc.buy}>
          Get the Rules
        </Link>

        <Divider className={toc.divider} />

        <DialogDisclosure store={store} className={toc.close}>
          Close
        </DialogDisclosure>
      </Dialog>

      <DialogDisclosure className={toc.trigger}>
        Table of Contents
      </DialogDisclosure>
    </DialogProvider>
  );
}

export function QuickStartPage() {
  useScrollToAnchor();

  return (
    <HeadingLevel>
      <PageContainer>
        <DocumentTitle>Quick Start Rules</DocumentTitle>

        <div className={page.container}>
          <TableOfContents />

          <div className={page.imageContainer}>
            <div className={page.image}>
              <PageHeader>
                <HeaderBackButton backHref={hrefs.rules()} theme="light" />
                <></>
              </PageHeader>
            </div>
          </div>

          <div className={page.content}>
            <div id="top" className={header.container}>
              <PageHeader className={header.nav}>
                <HeaderBackButton backHref={hrefs.rules()} />
                <HeaderTitle>Space Gits</HeaderTitle>
              </PageHeader>
              <Heading className={header.title}>Quick Start Rules</Heading>
            </div>

            <Section>
              <p className={header.intro}>
                Get stuck in with these Quick Start Rules. Everything you need
                to know to play your first few games of Space Gits. Want more?{" "}
                <Link href={hrefs.buy()} className={interactiveText}>
                  Grab the full rulebook
                </Link>
                .
              </p>
            </Section>

            <HeadingLevel>
              <SetupSection />

              <TowerSection />

              <ActivationSection />

              <ScootSection />

              <FightSection />

              <ShootSection />

              <BootSection />

              <EndSection />

              <BuySection />
            </HeadingLevel>
          </div>
        </div>
      </PageContainer>
    </HeadingLevel>
  );
}
